import {useMutation} from '@apollo/react-hooks';

import type {
    GetLeadChannels as GetLeadChannelsQuery,
    LeadRemoveSource as LeadRemoveSourceMutation,
    LeadRemoveSourceVariables as LeadRemoveSourceMutationVariables,
    LeadRemoveSourceInput,
} from 'nutshell-graphql-types';

import LEAD_REMOVE_SOURCE from '../graphql/mutations/lead-remove-source.graphql';
import GET_LEAD_CHANNELS from '../graphql/queries/get-lead-channels.graphql';

export function useRemoveSource(): (arg1: LeadRemoveSourceInput) => Promise<any> {
    const [removeSource] = useMutation<LeadRemoveSourceMutation, LeadRemoveSourceMutationVariables>(
        LEAD_REMOVE_SOURCE
    );

    return (input: LeadRemoveSourceInput) => {
        return removeSource({
            variables: {input},
            optimisticResponse: {
                leadRemoveSource: {
                    __typename: 'LeadRemoveSourcePayload',
                    lead: {
                        __typename: 'Lead',
                        id: input.leadId,
                    },
                },
            },
            update: (proxy) => {
                // Find the lead channels from the cache
                const cacheResult: GetLeadChannelsQuery | null | undefined = proxy.readQuery({
                    query: GET_LEAD_CHANNELS,
                    variables: {id: input.leadId},
                });

                if (cacheResult && cacheResult.lead && cacheResult.lead.channels) {
                    // Defined at upper level for singular proxy write at end of block
                    const additionalSources = cacheResult.lead.channels.sources;
                    const leadChannels = cacheResult.lead.channels.edges;

                    // First check if the source is in the additional sources
                    let sourceIndex = additionalSources.findIndex(
                        (source) => source.id === input.sourceId
                    );

                    if (sourceIndex !== -1) {
                        // Remove the source from additional sources
                        additionalSources.splice(sourceIndex, 1);
                    } else {
                        // Find which channel the source is in
                        const channelIndex = leadChannels.findIndex((channel) => {
                            const channelSourceIndex = channel.sources.findIndex(
                                (source) => source.id === input.sourceId
                            );
                            if (channelSourceIndex !== -1) {
                                // Since we're here, grab the source index too
                                sourceIndex = channelSourceIndex;

                                return true;
                            }

                            return false;
                        });

                        if (channelIndex !== -1 && sourceIndex !== -1) {
                            // Remove the source from the channel
                            // @ts-expect-error
                            leadChannels[channelIndex].sources.splice(sourceIndex, 1);
                        }
                    }

                    // Update the cache with new data
                    proxy.writeQuery({
                        query: GET_LEAD_CHANNELS,
                        variables: input.leadId,
                        data: {
                            lead: {
                                ...cacheResult.lead,
                                channels: {
                                    ...cacheResult.lead.channels,
                                    edges: [...leadChannels],
                                    sources: [...additionalSources],
                                },
                            },
                        },
                    });
                }
            },
        });
    };
}
